/**
 * ProjectListSettings
 *
 * Settings panel for the Project List view, housed inside the
 * Project Settings > Project List tab.
 *
 * Currently manages: which columns appear in the collapsed group header.
 */

import { LayoutList, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  useGroupHeaderSettings,
  SUBTOTALABLE_COLUMN_IDS,
  type SubtotalableColumnId,
} from '@/hooks/use-group-header-settings';

/**
 * Human-readable labels for each subtotalable column.
 * These MUST match the `header` values defined in projects-grid.tsx exactly.
 */
const COLUMN_LABELS: Record<SubtotalableColumnId, string> = {
  'budget':                 'Sales Budget',
  'actual-sales':           'Actual Sales',
  'sales-variance':         'Sales Variance',
  'cost-budget':            'Cost Budget',
  'actual-cost':            'Actual Costs',
  'cost-variance':          'Cost Variance',
  'gross-profit':           'Gross Profit',
  'wip-amount':             'WIP Amount',
  'forecast-at-completion': 'Forecast at Completion',
  'allocated-hours':        'Resource Budget (hrs)',
  'actual-hours':           'Actual Hours',
  'hours-variance':         'Hours Variance',
};

/** Group the columns into logical sections for easier scanning */
const COLUMN_GROUPS: { label: string; ids: SubtotalableColumnId[] }[] = [
  {
    label: 'Sales',
    ids: ['budget', 'actual-sales', 'sales-variance'],
  },
  {
    label: 'Costs',
    ids: ['cost-budget', 'actual-cost', 'cost-variance'],
  },
  {
    label: 'Profitability',
    ids: ['gross-profit', 'wip-amount', 'forecast-at-completion'],
  },
  {
    label: 'Hours',
    ids: ['allocated-hours', 'actual-hours', 'hours-variance'],
  },
];

const MAX_COLUMNS = 3;

export function ProjectListSettings() {
  const {
    selectedColumnIds,
    toggleColumn,
    resetToDefaults,
    hasUnsavedChanges,
    saveSettings,
    discardChanges,
  } = useGroupHeaderSettings();

  const handleSave = () => {
    saveSettings();
    toast.success('Project list settings saved.');
  };

  const handleDiscard = () => {
    discardChanges();
    toast.info('Changes discarded.');
  };

  const handleReset = () => {
    resetToDefaults();
  };

  return (
    <div className="space-y-6">
      {/* Page intro */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Project List Settings</h2>
        <p className="text-muted-foreground mt-1">
          Configure how the Project List view behaves for all users.
        </p>
      </div>

      {/* Group Header Columns card */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              <LayoutList className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle>Group Header Summary Columns</CardTitle>
              <CardDescription className="mt-1">
                When the Project List is grouped, each collapsed group header shows a quick
                financial summary. Choose up to <strong>{MAX_COLUMNS} columns</strong> to display.
                These columns are shown regardless of whether those columns are individually
                visible in the table — giving a consistent at-a-glance summary for all users.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">

          {/* Current selection preview */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Currently selected:</span>
            {selectedColumnIds.length === 0 ? (
              <span className="text-sm text-muted-foreground italic">None</span>
            ) : (
              selectedColumnIds.map((id, idx) => (
                <Badge key={id} variant="secondary" className="gap-1.5">
                  <span className="text-xs font-mono text-muted-foreground">{idx + 1}</span>
                  {COLUMN_LABELS[id]}
                </Badge>
              ))
            )}
            {selectedColumnIds.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({selectedColumnIds.length}/{MAX_COLUMNS} selected)
              </span>
            )}
          </div>

          <Separator />

          {/* Column picker — grouped */}
          <div className="space-y-5">
            {COLUMN_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {group.label}
                </p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {group.ids.map((id) => {
                    const isChecked = selectedColumnIds.includes(id);
                    const isDisabled =
                      !isChecked && selectedColumnIds.length >= MAX_COLUMNS;
                    const position = selectedColumnIds.indexOf(id);

                    return (
                      <div
                        key={id}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2.5 transition-colors cursor-pointer
                          ${isChecked
                            ? 'border-primary/40 bg-primary/5'
                            : isDisabled
                              ? 'border-border bg-muted/30 opacity-50 cursor-not-allowed'
                              : 'border-border hover:border-primary/30 hover:bg-muted/30'
                          }`}
                        onClick={() => {
                          if (!isDisabled) toggleColumn(id);
                        }}
                      >
                        <Checkbox
                          id={`col-${id}`}
                          checked={isChecked}
                          disabled={isDisabled}
                          onCheckedChange={() => {
                            if (!isDisabled) toggleColumn(id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Label
                          htmlFor={`col-${id}`}
                          className={`flex-1 text-sm cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}
                          onClick={(e) => e.preventDefault()}
                        >
                          {COLUMN_LABELS[id]}
                        </Label>
                        {isChecked && (
                          <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                            {position + 1}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {selectedColumnIds.length >= MAX_COLUMNS && (
            <Alert variant="info">
              <AlertDescription>
                You've selected the maximum of {MAX_COLUMNS} columns. Deselect one to choose a different column.
              </AlertDescription>
            </Alert>
          )}

          {/* Reset to defaults */}
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground gap-1.5"
              onClick={handleReset}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>

            {hasUnsavedChanges && (
              <div className="flex items-center gap-3">
                <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                <Button variant="outline" size="sm" onClick={handleDiscard}>
                  Discard
                </Button>
                <Button size="sm" onClick={handleSave}>
                  Save Settings
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert variant="info">
        <AlertDescription>
          <strong>Note:</strong> The columns shown in the group header summary are
          independent of which columns are visible in the project list table. A column
          can appear in the group header even if it has been hidden from the main table,
          ensuring all users always see a consistent financial summary when browsing grouped projects.
        </AlertDescription>
      </Alert>
    </div>
  );
}
